<script setup lang="ts">
import { onMounted, ref, unref, computed, nextTick } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import { AdminWidget, DashboardWidget } from '@/interfaces/Dashboard';
import ConnxWidgetWrapper from '@/views/Welcome/Dashboard/ConnxWidgetWrapper.vue';
import ConnxModal from '@/components/Molecules/ConnxModal.vue';
import ConnxWidgetLibraryCard from '@/components/Atoms/ConnxWidgetLibraryCard.vue';
import ConnxAdminDashboardWidgetSettings from './ConnxAdminDashboardWidgetSettings.vue';
import ConnxHeaderQuickActions from '@/components/Molecules/ConnxHeaderQuickActions.vue';

const nameModel = ref('');
const widgetSettings = ref();
const saving = ref(false);
const mode = ref<'default' | 'editing' | 'previewing' | 'help'>('default');
const selectedWidget = ref<AdminWidget | null>(null);
const inUseModel = ref<boolean | string>('');
const widgets = ref<AdminWidget[] | null>();

const filteredWidgets = computed<AdminWidget[]>(() => {
  let filteredResults = widgets.value as AdminWidget[];
  if (nameModel.value) {
    filteredResults = filteredResults.filter((i) => {
      const label = i?.widgetName?.toLowerCase();
      return label?.includes(nameModel.value.toLowerCase());
    });
  }
  if (inUseModel.value !== '') {
    filteredResults = filteredResults.filter((i) => {
      return i.isInUse === inUseModel.value;
    });
  }

  return filteredResults;
});

const dashboardStore = useDashboardStore();

const resetChanges = async () => {
  const temp = {
    ...selectedWidget.value,
  } as AdminWidget;
  selectedWidget.value = null;
  await nextTick();
  selectedWidget.value = temp;
};

const saveChanges = async () => {
  if (widgetSettings.value) {
    saving.value = true;
    const updatedWidget = await widgetSettings.value?.saveChanges();
    saving.value = false;
    // Update the current list of widgets instead of fetching new ones.
    if (updatedWidget && widgets.value) {
      widgets.value = widgets.value.map((w) => {
        if (w.guid !== updatedWidget?.guid) return w;
        return updatedWidget;
      });
      selectedWidget.value = null;
    }
  }
};

const resetSelection = () => {
  selectedWidget.value = null;
  mode.value = 'default';
};

const openSettings = (widget: AdminWidget) => {
  selectedWidget.value = widget;
  mode.value = 'editing';
};

const displayHelpText = (widget: AdminWidget) => {
  selectedWidget.value = widget;
  mode.value = 'help';
};

const previewWidget = (widget: AdminWidget) => {
  selectedWidget.value = widget;
  mode.value = 'previewing';
};

onMounted(async () => {
  const data = await dashboardStore.getAdminWidgets();
  if (data) {
    widgets.value = data;
  }
});
</script>
<template>
  <Teleport to="#page-header">
    <div class="tw-flex tw-items-center tw-gap-4 mr-2 pb-2">
      <connx-field
        v-model="nameModel"
        :type="InputType.Text"
        name="type"
        class="tw-mb-0"
        placeholder="Search"
      ></connx-field>
      <connx-field
        v-model="inUseModel"
        :type="InputType.Select"
        label="In Use:"
        label-inline
        class="tw-mb-0"
        :options="[
          { label: '(not set)', value: '' },
          { label: 'In Use', value: true },
          { label: 'Not in Use', value: false },
        ]"
        name="in-use"
      ></connx-field>
      <ConnxHeaderQuickActions disable-print />
    </div>
  </Teleport>
  <div>
    <div v-if="filteredWidgets && filteredWidgets.length === 0">
      <div v-if="nameModel !== '' && inUseModel === ''">
        <p
          >No widgets that have a title including <strong>{{ nameModel }}</strong></p
        >
      </div>
      <div v-if="nameModel === '' && inUseModel !== ''">
        <p
          >No widgets that are <strong>{{ inUseModel ? 'In use' : 'Not in use' }}</strong></p
        >
      </div>
      <div v-if="nameModel !== '' && inUseModel !== ''">
        <p
          >No widgets that have a title including <strong>{{ nameModel }}</strong> that are set to
          <strong>{{ inUseModel ? 'In use' : 'Not in use' }}</strong></p
        >
      </div>
    </div>
    <div
      class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-10 tw-overflow-visible"
    >
      <connx-widget-library-card
        v-for="widget in filteredWidgets"
        :widget="widget"
        :selected="selectedWidget === widget"
        @open:settings="openSettings(widget)"
        @open:help="displayHelpText(widget)"
        @open:preview="previewWidget(widget)"
      ></connx-widget-library-card>
    </div>
  </div>
  <connx-modal
    :open="selectedWidget !== null && mode === 'previewing'"
    title="Widget Preview"
    size="medium"
    @close="resetSelection"
  >
    <div v-if="selectedWidget" class="tw-h-144 tw-w-full">
      <connx-widget-wrapper
        :library-widget="selectedWidget"
        :cols="selectedWidget?.defaultJsonProperties.h!"
        :row-height="115"
        :settings-open="false"
      >
      </connx-widget-wrapper>
    </div>
  </connx-modal>

  <connx-modal
    scrollable
    type="side"
    title="Help"
    :open="selectedWidget !== null && mode === 'help'"
    @close="resetSelection"
  >
    <div v-if="selectedWidget?.helpFieldText" v-html="selectedWidget.helpFieldText"> </div>
    <template #footer>
      <div class="tw-flex tw-items-center tw-justify-center">
        <connx-button button-style="secondary" @click="resetSelection">Close</connx-button>
      </div>
    </template>
  </connx-modal>

  <connx-modal
    scrollable
    type="side"
    title="Settings"
    :open="selectedWidget !== null && mode === 'editing'"
    @close="resetSelection"
  >
    <div v-if="selectedWidget">
      <connx-admin-dashboard-widget-settings :widget="selectedWidget" ref="widgetSettings">
      </connx-admin-dashboard-widget-settings>
    </div>
    <template #footer>
      <div class="tw-flex tw-items-center tw-justify-between">
        <connx-button button-style="secondary" :disabled="saving" @click="selectedWidget = null"> Cancel </connx-button>
        <div class="tw-flex tw-items-center tw-justify-end tw-gap-x-4">
          <connx-button
            button-style="secondary"
            :disabled="saving || !widgetSettings?.hasChanges"
            @click="resetChanges"
          >
            Reset
          </connx-button>
          <connx-button
            button-style="success"
            :disabled="saving || !widgetSettings?.hasChanges || widgetSettings?.hasErrors"
            icon-class="tw-animate-spin"
            :icon="saving ? 'fad fa-spinner-third' : null"
            @click="saveChanges"
          >
            <span v-if="saving">Saving...</span>
            <span v-else>Save Changes</span>
          </connx-button>
        </div>
      </div>
    </template>
  </connx-modal>
</template>
